/**
 * Local draft attachment state for the agentic-room composer.
 *
 * Lifecycle:
 *   1. Component mounts — `stagingId` is generated once per draft.
 *   2. User picks file(s) — each goes into `items` with `status: 'uploading'`.
 *   3. We presign + PUT in parallel; on success the item flips to `ready`
 *      and we keep its descriptor (bucket/s3Key/filename/...). The execute
 *      payload reads `descriptors` directly.
 *   4. After Send succeeds the parent calls `reset()` which (a) clears
 *      `items` and (b) rotates the `stagingId` so a follow-up draft does
 *      not collide with the keys the backend just moved.
 *   5. After Send fails, the parent does *not* reset — the user still has
 *      their text + attachments and can retry.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  newAttachmentStagingId,
  uploadAttachmentForStaging,
} from '@/services/networkAttachmentApi';

let __nextItemId = 1;

function freshItem(file) {
  __nextItemId += 1;
  return {
    id: `att-${__nextItemId}-${Date.now().toString(36)}`,
    file,
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    size: file.size,
    status: 'uploading',
    percent: 0,
    descriptor: null,
    error: null,
  };
}

export function useChatAttachments(networkId) {
  const [items, setItems] = useState([]);
  const stagingIdRef = useRef(newAttachmentStagingId());

  const updateItem = useCallback((id, patch) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const addFiles = useCallback(
    async (files) => {
      const list = Array.from(files || []);
      if (!list.length || !networkId) return;
      const queued = list.map(freshItem);
      setItems((prev) => [...prev, ...queued]);
      const stagingId = stagingIdRef.current;
      await Promise.all(
        queued.map(async (it) => {
          try {
            const descriptor = await uploadAttachmentForStaging(networkId, it.file, stagingId, {
              onProgress: ({ percent }) => updateItem(it.id, { percent }),
            });
            updateItem(it.id, { status: 'ready', descriptor, percent: 100 });
          } catch (err) {
            updateItem(it.id, {
              status: 'error',
              error: err?.message || 'Upload failed',
            });
          }
        }),
      );
    },
    [networkId, updateItem],
  );

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const reset = useCallback(() => {
    setItems([]);
    stagingIdRef.current = newAttachmentStagingId();
  }, []);

  const descriptors = useMemo(
    () => items.filter((it) => it.status === 'ready' && it.descriptor).map((it) => it.descriptor),
    [items],
  );
  const hasUploading = useMemo(() => items.some((it) => it.status === 'uploading'), [items]);
  const hasError = useMemo(() => items.some((it) => it.status === 'error'), [items]);

  return {
    items,
    addFiles,
    removeItem,
    reset,
    stagingId: stagingIdRef.current,
    descriptors,
    hasUploading,
    hasError,
  };
}
